import { FC, PropsWithChildren, useState } from "react"
import { Link, Outlet, useLocation } from "react-router-dom"
import { gql } from "~/__generated__"
import { GroupVisibility } from "~/__generated__/graphql"
import { groupsPath, publicGroupsPath } from "~/common/paths"
import { useQuery } from "@apollo/client"
import plusSquareIcon from "~/images/plus-square-icon.svg"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "~/ui/dialog"
import { GroupForm } from "./group-form"

type BadgeProps = PropsWithChildren<{
  variant?: "blue" | "gray"
}>

const Badge: FC<BadgeProps> = ({ children, variant = "blue" }) => {
  const bgColor = variant === "blue" ? "bg-brand" : "bg-gray-100"
  const textColor = variant === "blue" ? "text-white" : "text-gray-900"

  return (
    <span className={`ml-2 rounded-full ${bgColor} px-2 py-1 text-xs ${textColor}`}>
      {children}
    </span>
  )
}

type ActiveTabIndicatorProps = {
  active: boolean
}

const ActiveTabIndicator: FC<ActiveTabIndicatorProps> = ({ active }) => {
  if (!active) return null
  return <div className="absolute bottom-0 left-0 right-0 -mb-[1px] h-[1px] bg-gray-900" />
}

const GROUP_COUNTS_QUERY = gql(/* GraphQL */ `
  query GroupCounts {
    myGroups: groups(first: 1, filters: { preset: MEMBER }) {
      totalCount
    }
    publicGroups: groups(first: 1, filters: { preset: PUBLIC }) {
      totalCount
    }
  }
`)

export const GroupsLayout = () => {
  const location = useLocation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data } = useQuery(GROUP_COUNTS_QUERY)

  return (
    <div className="container py-6">
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-medium">Groups</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button className="flex">
                <img src={plusSquareIcon} className="self-center" width={18} height={18} />
                <span className="pl-2">Add Group</span>
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Group</DialogTitle>
              </DialogHeader>
              <GroupForm
                initialVisibility={GroupVisibility.Private}
                onSuccess={() => {
                  setDialogOpen(false)
                }}
                onClose={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        <div className="border-b border-gray-200">
          <div className="flex h-10 items-center">
            <div className="flex items-center">
              <Link
                to={groupsPath({})}
                className={`relative px-4 py-2 ${
                  location.pathname === groupsPath({}) ? "font-medium" : "text-gray-500"
                }`}
              >
                My Groups
                <ActiveTabIndicator active={location.pathname === groupsPath({})} />
                <Badge variant={location.pathname === groupsPath({}) ? "blue" : "gray"}>
                  {data?.myGroups?.totalCount || 0}
                </Badge>
              </Link>
              <Link
                to={publicGroupsPath({})}
                className={`relative px-4 py-2 ${
                  location.pathname === publicGroupsPath({}) ? "font-medium" : "text-gray-500"
                }`}
              >
                Public Groups
                <ActiveTabIndicator active={location.pathname === publicGroupsPath({})} />
                <Badge variant={location.pathname === publicGroupsPath({}) ? "blue" : "gray"}>
                  {data?.publicGroups?.totalCount || 0}
                </Badge>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Outlet />
    </div>
  )
}
